import React from "react";
import styled from "styled-components";
import {Row, Col} from "styled-bootstrap-grid";
import breakpoints from "../../styles/breakpoints";
import colors from "../../styles/colors";
import fonts from "../../styles/fonts";
import ExternalLinkButton from "../Core/ExternalLinkButton";
import ExternalLinkButtonWhite from "../Core/ExternalLinkButtonWhite";

const ReasonsContainer = styled.div`
  text-align: center;
  margin-bottom: 100px;

  @media(min-width: ${breakpoints.md}){
    margin-bottom: 150px;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  line-height: 34px;
  ${fonts.montserratBold};
  margin-bottom: 10px;
  color: ${colors.blackDark};

  @media(min-width: ${breakpoints.md}){
    font-size: 30px;
    line-height: 44px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  line-height: 26px;
  ${fonts.montserratRegular};
  color: ${colors.blackDark};
  max-width: 552px;
  margin: 0 auto 40px auto;

  @media(min-width: ${breakpoints.md}){
    font-size: 20px;
    line-height: 30px;
    margin-bottom: 60px;
  }
`;

const ReasonBlock = styled.div`
  background: ${colors.white};
  border-radius: 10px;
  padding: 30px 24px;
  text-align: left;
  max-width: 360px;
  margin: 0 auto 20px auto;
  height: calc(100% - 20px);

  @media(min-width: ${breakpoints.md}){
    max-width: unset;
    margin: 0 12px 24px 12px;
    height: calc(100% - 24px);
  }
`;

const ReasonTitle = styled.h3`
  font-size: 16px;
  line-height: 22px;
  ${fonts.montserratBold};
  color: ${colors.blackDark};
  margin-bottom: 12px;

  @media(min-width: ${breakpoints.md}){
    font-size: 18px;
    line-height: 24px;
  }
`;

const ReasonText = styled.p`
  font-size: 14px;
  line-height: 22px;
  ${fonts.montserratRegular};
  color: ${colors.black};

  @media(min-width: ${breakpoints.md}){
    font-size: 16px;
    line-height: 26px;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;

  a {
    margin-top: 20px;
    width: 100%;
    max-width: 360px;
    text-align: center;
  }

  @media(min-width: ${breakpoints.md}){
    flex-direction: row;
    justify-content: center;
    margin-top: 36px;

    a {
      width: auto;
      min-width: 192px;
      margin-top: 0;
      margin-left: 10px;
      margin-right: 10px;
    }
  }
`;

const reasons = [
  {
    title: "Preview every branch",
    text: "Each branch gets its own unpublished theme on your store, with a shareable preview link. Review changes with clients and teammates on real products before anything goes live."
  },
  {
    title: "Your theme settings are safe",
    text: "Before every deploy we pull the live theme's settings and carry them over, so changes made in the Shopify theme editor are never overwritten by a code deploy."
  },
  {
    title: "Ship to live with confidence",
    text: "Your published theme only updates from your production branch, and only when auto-publish is switched on. No accidental deploys to a live storefront."
  },
  {
    title: "No new tools to learn",
    text: "Keep working the way you already do: branches, commits, and pull requests on GitHub. Push code and Theme Deploy handles the rest — no CLI or CI config required."
  },
  {
    title: "Only the access it needs",
    text: "Theme Deploy requests theme permissions only — it can't touch your orders, customers, or products. GitHub access uses short-lived tokens scoped to your repository."
  },
  {
    title: "Set up in minutes, free to try",
    text: "Install the app, connect GitHub, pick a repository — your first deployment runs automatically. Every store starts with a 14-day free trial."
  }
];

const Reasons = () => {
  return (
    <ReasonsContainer>
      <Title>Why teams choose Theme Deploy</Title>
      <Subtitle>
        Stop uploading theme files by hand. Give your store a real deployment
        pipeline without changing how your team works.
      </Subtitle>
      <Row>
        {reasons.map((reason, index) => (
          <Col md={4} key={index}>
            <ReasonBlock>
              <ReasonTitle>{reason.title}</ReasonTitle>
              <ReasonText>{reason.text}</ReasonText>
            </ReasonBlock>
          </Col>
        ))}
      </Row>
      <Buttons>
        <ExternalLinkButton href={process.env.APP_URL}>Get Started</ExternalLinkButton>
        <ExternalLinkButtonWhite href={"/docs/"} sameTab={true}>Read the Docs</ExternalLinkButtonWhite>
      </Buttons>
    </ReasonsContainer>
  )
};

export default Reasons;
